import { sortBy } from "lodash";
import moment from "moment";
import { Fragment, useEffect, useRef, useState } from "react";
import { CalendarEventFragmentFragment } from "types/graphql-schema";

import {
  hourInPixels,
  minimumEventHeight,
  minuteInPixels,
  topPixelMargin,
} from "@apps/calendar/calendar";
import { currentUserVar } from "@cache/cache";

import DayColumn from "./day-column";

const Week = ({
  meetings,
  selectedDay,
  onClickMeetingLink,
}: {
  meetings: CalendarEventFragmentFragment[];
  selectedDay: string;
  onClickMeetingLink: (meetingId: number, meetingGroupId: number) => void;
}) => {
  // HOOKS
  const [, setNow] = useState(Date.now()); // used to refresh page and today UI: https://github.com/Topicflow/topicflow/issues/1089
  const currentUser = currentUserVar();
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (currentUser.status !== "demo") {
      // scroll to 8am
      setTimeout(() => {
        if (!container.current) {
          return;
        }
        const startOfDayInPixels = 8 * 60 * minuteInPixels;
        container.current.scrollTo({
          top: startOfDayInPixels,
          behavior: "smooth",
        });
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const si = setInterval(() => {
      setNow(Date.now());
    }, 1000 * 60); // every minute
    return function cleanup() {
      clearInterval(si);
    };
  }, []);

  const hourLabels = [
    "12AM",
    "1AM",
    "2AM",
    "3AM",
    "4AM",
    "5AM",
    "6AM",
    "7AM",
    "8AM",
    "9AM",
    "10AM",
    "11AM",
    "12PM",
    "1PM",
    "2PM",
    "3PM",
    "4PM",
    "5PM",
    "6PM",
    "7PM",
    "8PM",
    "9PM",
    "10PM",
    "11PM",
  ];
  const firstDayOfWeek = moment(selectedDay)
    .startOf("isoWeek")
    .format("YYYY-MM-DD");
  const lastDayOfWeek = moment(selectedDay)
    .endOf("isoWeek")
    .format("YYYY-MM-DD");
  const beforeSelectedDay = moment(selectedDay)
    .subtract(1, "day")
    .format("YYYY-MM-DD");
  const afterSelectedDay = moment(selectedDay)
    .add(1, "day")
    .format("YYYY-MM-DD");

  // we take meetings of current week and the previous/next day (used for 3 day view)
  const filterStart = moment(firstDayOfWeek).subtract(1, "day").startOf("day");
  const filterEnd = moment(lastDayOfWeek).add(1, "day").endOf("day");
  const filteredMeetings = meetings.filter((meeting) =>
    moment(meeting.startDatetime).isBetween(
      filterStart,
      filterEnd,
      undefined,
      "[]",
    ),
  );

  const sortedMeetings = sortBy(filteredMeetings, "startDatetime");
  const eventsOfWeek = sortBy(
    sortedMeetings.map((meeting) => {
      const duration = moment(meeting.endDatetime).diff(
        meeting.startDatetime,
        "minutes",
      );
      return {
        ...meeting,
        startInMinutes: moment(meeting.startDatetime).diff(
          moment(meeting.startDatetime).startOf("day"),
          "minutes",
        ),
        duration,
        weekDay: moment(meeting.startDatetime).isoWeekday(),
        height: Math.max(duration * minuteInPixels, minimumEventHeight),
      };
    }),
    "startInMinutes",
  );
  const threeDayEventsByDay = [
    beforeSelectedDay,
    selectedDay,
    afterSelectedDay,
  ].map((day) => {
    return {
      day: moment(day).format("YYYY-MM-DD"),
      events: eventsOfWeek.filter(({ startDatetime }) =>
        moment(startDatetime).isSame(day, "day"),
      ),
    };
  });

  // RENDER
  return (
    <div className="absolute inset-0 overflow-y-scroll" ref={container}>
      <div
        id="calendar"
        aria-label="Calendar week container"
        className="flex flex-auto flex-col"
      >
        <div
          style={{ width: "165%" }}
          className="flex max-w-full flex-none flex-col md:max-w-full"
        >
          <div className="flex flex-auto overflow-hidden">
            <div className="sticky left-0 z-10 w-14 flex-none bg-white ring-1 ring-gray-100" />
            <div className="grid flex-auto grid-cols-1 grid-rows-1">
              {/* Horizontal lines */}
              <div
                className="col-start-1 col-end-2 row-start-1 grid divide-y divide-gray-100"
                style={{
                  gridTemplateRows: `repeat(48, minmax(${
                    hourInPixels / 2
                  }px, 1fr))`,
                }}
              >
                <div
                  className="row-end-1"
                  style={{ height: topPixelMargin }}
                ></div>
                {hourLabels.map((hourLabel) => (
                  <Fragment key={hourLabel}>
                    <div>
                      <div className="sticky left-0 z-20 -mt-2.5 -ml-14 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                        {hourLabel}
                      </div>
                    </div>
                    <div />
                  </Fragment>
                ))}
              </div>

              {/* Vertical lines */}
              <div className="col-start-1 col-end-2 row-start-1 hidden grid-rows-1 divide-x divide-gray-100 @4xl/calendar:grid @4xl/calendar:grid-cols-7">
                <div className="col-start-1 row-span-full" />
                <div className="col-start-2 row-span-full" />
                <div className="col-start-3 row-span-full" />
                <div className="col-start-4 row-span-full" />
                <div className="col-start-5 row-span-full" />
                <div className="col-start-6 row-span-full" />
                <div className="col-start-7 row-span-full" />
                <div className="col-start-8 row-span-full w-8" />
              </div>

              <div className="relative col-start-1 col-end-2 row-start-1 grid grid-cols-1 @lg/calendar:grid-cols-3 @4xl/calendar:hidden divide-x">
                {threeDayEventsByDay.map(({ day, events }) => (
                  <DayColumn
                    events={events}
                    day={day}
                    key={day}
                    selectedDay={selectedDay}
                    onClickMeetingLink={onClickMeetingLink}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Week;
